import { useEffect, useState } from "react";
import { Info, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import TextvorlagenInlineManager from "@/components/TextvorlagenInlineManager";

/**
 * AuswahlFeld – gemeinsamer Baustein für Auswahllisten (A-010 M3e / A-005, 17.10.2026).
 *
 * Ralphs Grundsatz: Die Suite führt; bei jeder Auswahl direkt „verwalten / + neu“.
 *   • Auswahlliste (natives <select>, am Handy gut bedienbar), Liste aus module_textvorlagen (doc_type),
 *     bei langen Listen ein Suchfeld darüber,
 *   • „+ Neu …“ als Eintrag in der Liste: Name + optional Stichwörter, sofort gespeichert und ausgewählt,
 *   • „verwalten“ neben der Beschriftung (TextvorlagenInlineManager: umbenennen, löschen, Stichwörter),
 *   • Vorschlag (Stichwort-Treffer, siehe useStichwortVorschlag) als vorausgewählter Wert mit Hinweis
 *     „Vorschlag: … (erkannt: …)“; erst „Übernehmen“ oder eine Wahl speichert,
 *   • Wert, der in keiner Liste steht, wird gezeigt und mit Hinweis markiert (kein stiller Wert).
 *
 * Props:
 *   docType, label, value (String), onChange(neuerWert)
 *   ersatz       Liste, falls (noch) nichts gepflegt ist
 *   leer         Text der Leer-Option (z. B. "— keine —"); ohne leer gibt es keine Leer-Option
 *   vorschlag    Ergebnis von useStichwortVorschlag (optional)
 *   stichwoerter bei „+ Neu“ Stichwörter abfragen (Standard: ja)
 *   busy, hinweis (Knoten unter dem Feld), testId
 *
 * Belegtexte (BUT-0029, 29.10.2026) – für Betreff/Vortext/Schlusstext/E-Mail-Text (TextTemplateSelect):
 *   eintraege       fertig geladene Vorlagen [{title, content, keywords, sort_order}] statt eigener Abfrage
 *   onNeuAnlegen    async (titel, stichwoerter) – „+ Neu …“ legt die Vorlage selbst an (mit dem Text des Feldes)
 *   verwaltenKnoten ersetzt „verwalten“ (hier: großes Fenster mit Bearbeiten, Standard-Stern, Löschen)
 *   zusatz          { Titel: "Standard" } – Zusatz hinter dem Namen in der Liste
 *   immerSuche      Suchfeld immer zeigen; Treffer auch im Text der Vorlage
 */
const NEU = "__neu__";
// Ersatz für doc_type kunden_status, wie in „Kunde bearbeiten“ (pages/KundenModulPage.jsx)
export const KUNDEN_STATUS_ERSATZ = ["Anfrage", "Neu", "Interessent", "Kunde", "In Bearbeitung", "Aufmaß", "Angebot", "Auftrag", "Abgeschlossen", "Archiv"];
const SUCHE_AB = 8;
const sortiert = (liste) => [...liste].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || (a.title || "").localeCompare(b.title || ""));
const gleich = (a, b) => (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();
// Stichwörter eines Eintrags: Liste oder Komma-Text, beides vertragen (BUT-0021)
const alsWorte = (kw) => (Array.isArray(kw) ? kw : typeof kw === "string" ? kw.split(",") : []).map((w) => String(w).trim()).filter(Boolean);
const trefferText = (x) => (x === "steht im Text" ? x : `Stichwort: ${x}`);
const meldeAenderung = (docType) => window.dispatchEvent(new CustomEvent("textvorlagen-changed", { detail: { docType } }));

/** Stichwort-Vorschlag per /modules/textvorlagen/match. Liefert { best, kandidaten, tied, mitStichwoertern } oder null. */
export const useStichwortVorschlag = (text, docType, fallbackDocType = "") => {
  const [ergebnis, setErgebnis] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const neu = (e) => { if (!e?.detail?.docType || e.detail.docType === docType) setTick((t) => t + 1); };
    window.addEventListener("textvorlagen-changed", neu);
    return () => window.removeEventListener("textvorlagen-changed", neu);
  }, [docType]);
  useEffect(() => {
    const t = (text || "").trim();
    if (!t) { setErgebnis(null); return undefined; }
    let lebt = true;
    const body = { text: t, doc_type: docType, top_n: 5 };
    if (fallbackDocType) body.fallback_doc_type = fallbackDocType;
    api.post("/modules/textvorlagen/match", body)
      .then((r) => {
        if (!lebt) return;
        const d = r.data || {};
        const kand = d.candidates || [];
        setErgebnis({
          best: d.tied ? null : (d.best || null),
          kandidaten: d.tied ? kand.filter((c) => c.hits === kand[0]?.hits) : [],
          tied: !!d.tied,
          mitStichwoertern: d.mit_stichwoertern,
        });
      })
      .catch(() => { if (lebt) setErgebnis(null); });
    return () => { lebt = false; };
  }, [text, docType, fallbackDocType, tick]);
  return ergebnis;
};

const AuswahlFeld = ({
  docType, label, value = "", onChange, ersatz = [], leer = "", vorschlag = null,
  stichwoerter = true, busy = false, hinweis = null, testId,
  eintraege = null, onNeuAnlegen = null, verwaltenKnoten = null, zusatz = null, immerSuche = false,
}) => {
  const tid = testId || `auswahl-${docType}`;
  const eigeneListe = Array.isArray(eintraege);
  const [liste, setListe] = useState(ersatz);
  const [worte, setWorte] = useState({}); // Titel (klein) -> Stichwörter (BUT-0021)
  const [tick, setTick] = useState(0);
  const [suche, setSuche] = useState("");
  const [neuOffen, setNeuOffen] = useState(false);
  const [neuTitel, setNeuTitel] = useState("");
  const [neuWorte, setNeuWorte] = useState("");
  const [neuBusy, setNeuBusy] = useState(false);

  useEffect(() => {
    const neu = (e) => { if (!e?.detail?.docType || e.detail.docType === docType) setTick((t) => t + 1); };
    window.addEventListener("textvorlagen-changed", neu);
    return () => window.removeEventListener("textvorlagen-changed", neu);
  }, [docType]);
  // Belegtexte: Liste kommt fertig von außen (Belegart-Filter dort); gleiche Titel nur einmal
  const eintraegeSchluessel = eigeneListe ? JSON.stringify(eintraege.map((t) => [t.title, t.keywords, t.content])) : "";
  const inhaltVon = {};
  if (eigeneListe) eintraege.forEach((t) => { const k = (t.title || "").trim().toLowerCase(); if (k && !(k in inhaltVon)) inhaltVon[k] = t.content || ""; });
  useEffect(() => {
    if (!eigeneListe) return;
    const titel = [];
    const karte = {};
    eintraege.forEach((t) => {
      const k = (t.title || "").trim().toLowerCase();
      if (!k || k in karte) return;
      titel.push(t.title);
      karte[k] = alsWorte(t.keywords);
    });
    setListe(titel); setWorte(karte);
  }, [eintraegeSchluessel]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (eigeneListe) return undefined;
    let lebt = true;
    api.get(`/modules/textvorlagen/data?doc_type=${docType}`)
      .then((r) => {
        const eintraege = sortiert(r.data || []).filter((t) => t.title);
        const titel = eintraege.map((t) => t.title);
        const karte = {};
        eintraege.forEach((t) => { karte[t.title.trim().toLowerCase()] = alsWorte(t.keywords); });
        if (lebt) { setListe(titel.length > 0 ? titel : ersatz); setWorte(karte); }
      })
      .catch(() => { /* Ersatzliste bleibt */ });
    return () => { lebt = false; };
  }, [docType, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Vorschlag nur, solange nichts gewählt ist
  const vorBest = !value && vorschlag?.best && liste.some((l) => gleich(l, vorschlag.best.title)) ? vorschlag.best : null;
  const vorWahl = !value && vorschlag?.tied ? vorschlag.kandidaten : [];
  const angezeigt = value || vorBest?.title || "";
  const fremd = value && !liste.some((l) => gleich(l, value));
  // Suche (BUT-0021): Titel-Treffer zuerst, dann Stichwort-Treffer (mit Hinweis, welches Stichwort passt)
  const wortVon = (l) => worte[(l || "").trim().toLowerCase()] || [];
  const q = suche.trim().toLowerCase();
  const stichwortTreffer = {};
  let gefiltert = liste;
  let trefferAnzahl = liste.length;
  if (q) {
    const titelTreffer = liste.filter((l) => l.toLowerCase().includes(q));
    const worteTreffer = liste.filter((l) => {
      if (titelTreffer.includes(l)) return false;
      const w = wortVon(l).find((x) => x.toLowerCase().includes(q));
      if (w) stichwortTreffer[l] = w;
      else if (eigeneListe && (inhaltVon[(l || "").trim().toLowerCase()] || "").toLowerCase().includes(q)) stichwortTreffer[l] = "steht im Text";
      return !!stichwortTreffer[l];
    });
    gefiltert = [...titelTreffer, ...worteTreffer];
    trefferAnzahl = gefiltert.length;
    liste.forEach((l) => { if (!gefiltert.includes(l) && gleich(l, angezeigt)) gefiltert.push(l); });
  }
  const mitSuche = immerSuche || liste.length > SUCHE_AB || liste.some((l) => wortVon(l).length > 0);

  const waehlen = (wert) => {
    if (wert === NEU) { setNeuOffen(true); setNeuTitel(suche.trim()); return; }
    onChange?.(wert);
  };

  const neuAnlegen = async () => {
    const t = neuTitel.trim();
    if (!t) return;
    const vorhanden = liste.find((l) => gleich(l, t));
    if (vorhanden) {
      onChange?.(vorhanden);
      setNeuOffen(false); setNeuTitel(""); setNeuWorte(""); setSuche("");
      toast.info(`„${vorhanden}“ gibt es schon – ausgewählt`);
      return;
    }
    setNeuBusy(true);
    try {
      if (onNeuAnlegen) {
        await onNeuAnlegen(t, neuWorte);
      } else {
        await api.post("/modules/textvorlagen/data", { title: t, content: "", doc_type: docType, text_type: "titel", keywords: neuWorte });
        meldeAenderung(docType);
        setListe((l) => [...l, t]);
        onChange?.(t);
      }
      setNeuOffen(false); setNeuTitel(""); setNeuWorte(""); setSuche("");
      toast.success(`„${t}“ angelegt – gilt jetzt überall`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally {
      setNeuBusy(false);
    }
  };

  return (
    <div data-testid={tid}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <label className="block text-sm font-medium" htmlFor={`${tid}-select`}>{label}</label>
        {verwaltenKnoten || <TextvorlagenInlineManager docType={docType} label={label} onChanged={() => meldeAenderung(docType)} />}
      </div>

      {mitSuche && (
        <input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder={`${label} suchen (auch Stichwort) …`}
          className="mb-1.5 h-8 w-full rounded-sm border border-input bg-background px-2 text-xs" data-testid={`${tid}-suche`} />
      )}
      {mitSuche && q && (
        <div className="mb-1.5 flex flex-wrap items-center gap-1.5 text-xs" data-testid={`${tid}-treffer`}>
          {trefferAnzahl === 0 ? (
            <span className="text-muted-foreground">Nichts gefunden zu „{suche.trim()}“</span>
          ) : gefiltert.slice(0, trefferAnzahl).slice(0, 8).map((l) => (
            <button key={l} type="button" disabled={busy} onClick={() => { onChange?.(l); setSuche(""); }}
              className="px-2 py-1 rounded-full border border-input bg-muted/40 hover:bg-muted text-left break-words max-w-full" data-testid={`${tid}-treffer-${l}`}>
              {l}{stichwortTreffer[l] ? <span className="text-muted-foreground"> · {trefferText(stichwortTreffer[l])}</span> : null}
            </button>
          ))}
        </div>
      )}

      <select
        id={`${tid}-select`}
        value={angezeigt}
        disabled={busy}
        onChange={(e) => waehlen(e.target.value)}
        className={`w-full h-10 rounded-sm border bg-background px-3 text-sm disabled:opacity-60 ${vorBest ? "border-amber-400 border-dashed ring-1 ring-amber-300" : "border-input"}`}
        data-testid={`${tid}-select`}
        data-vorschlag={vorBest ? "ja" : "nein"}
      >
        {(leer || !angezeigt) && <option value="">{leer || "— bitte wählen —"}</option>}
        {fremd && <option value={value}>{value} (steht in keiner Liste)</option>}
        {gefiltert.map((s) => <option key={s} value={s}>{s}{zusatz?.[s] ? ` (${zusatz[s]})` : ""}{vorBest && gleich(s, vorBest.title) ? " (Vorschlag)" : ""}{stichwortTreffer[s] ? ` (${trefferText(stichwortTreffer[s])})` : ""}</option>)}
        {q && trefferAnzahl === 0 && <option value="" disabled>Nichts gefunden zu „{suche.trim()}“</option>}
        <option value={NEU}>+ Neu …</option>
      </select>

      {vorBest && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2 rounded-sm border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200" data-testid={`${tid}-vorschlag`}>
          <Sparkles className="w-3.5 h-3.5 shrink-0" />
          <span className="min-w-0 flex-1 break-words">Vorschlag: <b>{vorBest.title}</b> (erkannt: {(vorBest.matched_terms || []).join(", ")})</span>
          <button type="button" disabled={busy} onClick={() => onChange?.(vorBest.title)}
            className="shrink-0 px-2 py-1 rounded-sm border border-amber-400 bg-white/60 hover:bg-amber-100 font-medium disabled:opacity-50" data-testid={`${tid}-vorschlag-uebernehmen`}>
            Übernehmen
          </button>
        </div>
      )}
      {vorWahl.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 rounded-sm border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200" data-testid={`${tid}-mehrere`}>
          <Sparkles className="w-3.5 h-3.5 shrink-0" />
          <span>Mehrere passen gleich gut – bitte wählen:</span>
          {vorWahl.map((c) => (
            <button key={c.title} type="button" disabled={busy} onClick={() => onChange?.(c.title)}
              className="px-2 py-0.5 rounded-full border border-amber-400 bg-white/60 hover:bg-amber-100 disabled:opacity-50"
              title={`erkannt: ${(c.matched_terms || []).join(", ")}`} data-testid={`${tid}-kandidat-${c.title}`}>
              {c.title}
            </button>
          ))}
        </div>
      )}
      {!value && vorschlag && !vorBest && vorWahl.length === 0 && (
        <div className="mt-1 flex items-start gap-1.5 text-[11px] text-muted-foreground" data-testid={`${tid}-kein-vorschlag`}>
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            {vorschlag.mitStichwoertern
              ? "Kein Vorschlag: Im Text steht keins der Stichwörter. Bitte selbst wählen."
              : "Kein Vorschlag möglich: noch keine Stichwörter hinterlegt („verwalten“ → Stift → Stichwörter)."}
          </span>
        </div>
      )}
      {fremd && (
        <div className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-800 dark:text-amber-300" data-testid={`${tid}-fremd`}>
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>„{value}“ steht nicht in der Liste. Bitte einen Eintrag wählen oder unter „+ Neu …“ anlegen.</span>
        </div>
      )}

      {neuOffen && (
        <div className="mt-1.5 space-y-1.5 rounded-sm border border-emerald-300 bg-emerald-50/60 p-2 dark:bg-emerald-950/30" data-testid={`${tid}-neu`}>
          <input autoFocus value={neuTitel} onChange={(e) => setNeuTitel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); neuAnlegen(); } if (e.key === "Escape") setNeuOffen(false); }}
            placeholder={`Neuer Eintrag für „${label}“`} className="h-9 w-full rounded-sm border border-input bg-background px-2 text-sm" data-testid={`${tid}-neu-titel`} />
          {stichwoerter && (
            <input value={neuWorte} onChange={(e) => setNeuWorte(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); neuAnlegen(); } }}
              placeholder="Stichwörter (optional, mit Komma), z. B. balkontür, gekippt" className="h-9 w-full rounded-sm border border-input bg-background px-2 text-sm" data-testid={`${tid}-neu-worte`} />
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setNeuOffen(false)} className="h-8 px-3 rounded-sm border text-xs">Abbrechen</button>
            <button type="button" onClick={neuAnlegen} disabled={!neuTitel.trim() || neuBusy}
              className="h-8 px-3 rounded-sm bg-emerald-600 text-white text-xs disabled:opacity-50" data-testid={`${tid}-neu-speichern`}>
              {neuBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Anlegen und wählen"}
            </button>
          </div>
        </div>
      )}
      {hinweis}
    </div>
  );
};

export default AuswahlFeld;
